import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { 
  AcademicCapIcon,
  PencilIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  UserGroupIcon,
  CalendarIcon,
  InformationCircleIcon,
  DocumentTextIcon,
  DocumentCheckIcon,
  IdentificationIcon
} from "@heroicons/react/24/outline";
import { HsePageChrome } from "../../../../components/dashboard/hse/HsePageChrome";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { ResourceError } from "../../../../components/utils/ResourceError";
import { ResourceEmpty } from "../../../../components/utils/ResourceEmpty";
import { errorStateVariantFromError } from "../../../../components/utils/errorStateUtils";
import RoleOnly, { useUserHasAccess } from "../../../../routes/roleOnly";
import { getCompetencyDefinition } from "../../../../api/hse/competency";
import { toast } from "react-hot-toast";
import { CompetencySubnav } from "../../../../components/dashboard/hse/competency/CompetencySubnav";

/**
 * HSE/KS Competency Definition View - View competency definition
 * ISO 9001:2015 Clause 7.2 - Competence
 */
export const HseKsCompetencyDefinitionView = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  
  const [definition, setDefinition] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorVariant, setErrorVariant] = useState("load_error");
  
  const canManage = useUserHasAccess({ role: "hse_ks_competency_manage", action: "read" });

  useEffect(() => {
    fetchDefinition();
  }, [id]);

  const fetchDefinition = async () => {
    try {
      setLoading(true);
      setError(false);
      
      const response = await getCompetencyDefinition(id);
      setDefinition(response.competency_definition || response);
      setEmployees(response.employees || []);
    } catch (err) {
      console.error("Error fetching definition:", err);
      const variant = errorStateVariantFromError(err);
      setError(true);
      setErrorVariant(variant);
      if (variant === "load_error") {
        toast.error(t("common.error_state.load_error.title"));
      }
    } finally {
      setLoading(false);
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case "cv":
        return <DocumentTextIcon className="h-5 w-5 text-blue-600" />;
      case "certificate":
        return <DocumentCheckIcon className="h-5 w-5 text-green-600" />;
      case "course":
        return <AcademicCapIcon className="h-5 w-5 text-purple-600" />;
      case "license":
        return <IdentificationIcon className="h-5 w-5 text-orange-600" />;
      default:
        return <DocumentTextIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getTypeLabel = (type) => {
    const types = {
      cv: t("CV"),
      certificate: t("Sertifikat"),
      course: t("Kurs"),
      license: t("Lisens"),
      other: t("Annet"),
    };
    return types[type] || type;
  };

  if (loading) {
    return <ResourceLoading size="base" />;
  }

  if (error) {
    return (
      <ResourceError
        variant={errorVariant}
        onRetry={errorVariant === "load_error" ? fetchDefinition : undefined}
        backTo="/dashboard/hse-ks/competency/definitions"
      />
    );
  }

  if (!definition) {
    return (
      <ResourceError
        variant="not_found"
        backTo="/dashboard/hse-ks/competency/definitions"
      />
    );
  }

  return (
    <HsePageChrome
      title={definition.name || t("Kompetanse")}
      subtitle={t("Kompetansedefinisjon")}
      backTo="/dashboard/hse-ks/competency/definitions"
      mainButton={
        canManage
          ? {
              label: t("Rediger"),
              icon: PencilIcon,
              path: `/dashboard/hse-ks/competency/definitions/${id}/edit`,
            }
          : undefined
      }
    >
      <div className="mx-auto mt-8 max-w-4xl sm:px-6 lg:px-8">
        <CompetencySubnav />
        <div className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">{t("Grunnleggende informasjon")}</h3>
                {canManage && (
                  <Link
                    to={`/dashboard/hse-ks/competency/definitions/${id}/edit`}
                    className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PencilIcon className="h-4 w-4 mr-1" />
                    {t("Rediger")}
                  </Link>
                )}
              </div>
              
              <dl className="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t("Navn")}</dt>
                  <dd className="mt-1 text-sm text-gray-900 flex items-center">
                    {getTypeIcon(definition.type)}
                    <span className="ml-2">{definition.name}</span>
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t("Type")}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                      {getTypeLabel(definition.type)}
                    </span>
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t("Kategori")}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {definition.category || t("Ikke kategorisert")}
                    </span>
                  </dd>
                </div>
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t("Gyldighetsperiode")}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {definition.requires_renewal && definition.renewal_interval_days
                      ? `${definition.renewal_interval_days} ${t("dager")}`
                      : t("Permanent")
                    }
                  </dd>
                </div>
                
                {definition.requires_renewal && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">{t("Fornyelsesintervall")}</dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {definition.renewal_interval_days 
                        ? `${definition.renewal_interval_days} ${t("dager")}`
                        : t("Ikke spesifisert")
                      }
                    </dd>
                  </div>
                )}
                
                <div>
                  <dt className="text-sm font-medium text-gray-500">{t("Status")}</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {definition.is_active ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="h-3 w-3 mr-1" />
                        {t("Aktiv")}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {t("Inaktiv")}
                      </span>
                    )}
                  </dd>
                </div>
                
                {definition.description && (
                  <div className="sm:col-span-2">
                    <dt className="text-sm font-medium text-gray-500">{t("Beskrivelse")}</dt>
                    <dd className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">{definition.description}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Requirements */}
          {definition.requirements && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Krav")}</h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{definition.requirements}</p>
              </div>
            </div>
          )}

          {/* Assessment Criteria */}
          {definition.assessment_criteria && (
            <div className="bg-white shadow sm:rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">{t("Vurderingskriterier")}</h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{definition.assessment_criteria}</p>
              </div>
            </div>
          )}

          {/* Employees with this competency */}
          <div className="bg-white shadow sm:rounded-lg">
            <div className="px-4 py-5 sm:p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t("Ansatte med denne kompetansen")} ({employees.length})
              </h3>
              
              {employees.length === 0 ? (
                <ResourceEmpty message={t("Ingen ansatte har denne kompetansen")} />
              ) : (
                <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-300">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                          {t("Ansatt")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Utstedt")}
                        </th>
                        <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                          {t("Utløper")}
                        </th>
                        <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                          <span className="sr-only">{t("Handlinger")}</span>
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {employees.map((employee) => (
                        <tr key={employee.id} className="hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm sm:pl-6">
                            <div className="flex items-center">
                              <UserGroupIcon className="h-5 w-5 text-blue-600 mr-3" />
                              <div>
                                <div className="font-medium text-gray-900">{employee.name || employee.email}</div>
                                {employee.department_name || employee.department ? (
                                  <div className="text-gray-500 text-xs mt-1">
                                    {employee.department_name || employee.department}
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {employee.issued_at ? (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(employee.issued_at).toLocaleDateString("nb-NO")}
                              </span>
                            ) : (
                              <span className="text-gray-400">-</span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            {employee.expires_at ? (
                              <span className="flex items-center">
                                <CalendarIcon className="h-4 w-4 mr-1" />
                                {new Date(employee.expires_at).toLocaleDateString("nb-NO")}
                              </span>
                            ) : (
                              <span className="text-gray-400">{t("Permanent")}</span>
                            )}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <Link
                              to={`/dashboard/hse-ks/competency/employees/${employee.id}`}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              {t("Vis")}
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </HsePageChrome>
  );
};

